import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { Temporal } from 'temporal-polyfill'
import { View, ViewContainer, CardError } from 'components'
import { PageHeader } from '@politechdev/blocks-design-system'
import { useRequest, useRoutePathParams } from 'hooks'
import {
  fetchShift,
  putShift,
  postPetitionPacket,
  postDigitalBatch,
} from 'requests/shifts'
import ShiftForm from './ShiftForm'
import Upload from './Upload'
import { DataEntryUploadStorage } from '../storageUtils'
import { getBatchForAutoSubmission } from '../ShiftDataEntry/utils'
import { SHIFT_TYPE } from '../constants'

const ShiftBatch = () => {
  const { t } = useTranslation()
  const [{ id: shiftId }] = useRoutePathParams()
  const history = useHistory()

  const [currentShift, setCurrentShift] = useState()
  const [county, setCounty] = useState()
  const [currentStep, setCurrentStep] = useState()
  const [digitalBatchErrorMessage, setDigitalBatchErrorMessage] = useState()

  const {
    makeRequest: submitDigitalBatchRequest,
    hasErrors: digitalBatchHasErrors,
    isLoading: batchLoading,
  } = useRequest(postDigitalBatch, {
    onSuccess: async () => {
      await DataEntryUploadStorage.removeFromStorage(shiftId)
      history.push(`/collect/voter_registration/shifts`)
    },
    onError: async e => {
      await DataEntryUploadStorage.removeFromStorage(shiftId)

      const errors = e.json.error
      if (errors.filename?.includes('already exists')) {
        setDigitalBatchErrorMessage(
          t("We can't upload this packet because the file name already exists.")
        )
      } else if (
        errors.forms?.some(form =>
          form.file_hash?.includes('has already been taken')
        )
      ) {
        setDigitalBatchErrorMessage(
          t("We can't upload this packet because it already exists.")
        )
      }
    },
  })

  const {
    makeRequest: createPetitionPacket,
    hasErrors: petitionPacketHasErrors,
    errors: petitionPacketErrors,
    isLoading: petitionLoading,
  } = useRequest(
    fileData =>
      postPetitionPacket({
        shift_id: currentShift.id,
        file_locator: fileData,
        county,
      }),
    {
      onSuccess: () => history.push(`/collect/petitions/shifts`),
    }
  )

  const {
    makeRequest: storeCoverInfo,
    hasErrors: converInfoHasErrors,
    isLoading: coverInfoLoading,
  } = useRequest(data => putShift(currentShift.id, data), {
    onSuccess: () => setCurrentStep('upload'),
  })

  const handleUpload = async files => {
    if (currentShift.type === SHIFT_TYPE.REGISTRATION) {
      for (const [i, file] of files.entries()) {
        // eslint-disable-next-line no-await-in-loop
        await DataEntryUploadStorage.addRecordToStorage(currentShift.id, i, {
          ...file.response.body.data,
          url: file.response.body.url,
        })
      }

      if (
        currentShift.turf.voter_registration_config.data_entry_sections.length
      ) {
        history.push(
          `/collect/voter_registration/shifts/${shiftId}/data_entry/1`
        )
      } else {
        submitDigitalBatchRequest(shiftId, getBatchForAutoSubmission(shiftId))
      }
    } else {
      createPetitionPacket(files[0].response.body.data)
    }
  }

  const {
    makeRequest,
    isLoading: shiftLoading,
    hasErrors,
  } = useRequest(fetchShift, {
    onSuccess: ({ shift }) => {
      setCurrentShift(shift)

      if (shift.type === SHIFT_TYPE.PETITION) {
        setCurrentStep('upload')
      } else {
        setCurrentStep('cover_sheet_data')
      }
    },
  })

  useEffect(() => {
    makeRequest(shiftId, {
      fields: [
        'id',
        'shift_start',
        'shift_end',
        'field_start',
        'field_end',
        'notes',
        'soft_count_cards_total_collected',
        'soft_count_cards_complete_collected',
        'soft_count_cards_incomplete_collected',
        'soft_count_cards_with_phone_collected',
        'soft_count_pre_registration_cards_collected',
        'custom_field_data',
        'status',
        'name',
        'type',
        { cover_sheet: ['file_url'] },
        { forms: ['id', 'file_url', 'file_locator', 'pledge_card_url'] },
        { petition_packet: ['id', 'file_url', 'file_locator'] },
        { location: ['id', 'name', 'street_address', { turf: 'name' }] },
        { canvasser: ['id', 'full_name'] },
        { turf: ['options', 'voter_registration_config', 'name'] },
      ],
    })
  }, [shiftId])

  return (
    <View>
      <PageHeader
        title={t(
          currentStep === 'cover_sheet_data'
            ? 'Packet information'
            : 'Upload scans'
        )}
      />
      <ViewContainer
        loading={
          shiftLoading || petitionLoading || batchLoading || coverInfoLoading
        }
      >
        <CardError
          hide={!hasErrors}
          message={t("We're unable to retrieve this shift")}
        />
        <CardError
          hide={!converInfoHasErrors}
          message={t("We're unable to save shift data")}
        />
        <CardError
          hide={!digitalBatchHasErrors}
          message={digitalBatchErrorMessage}
        />
        <CardError
          hide={!petitionPacketHasErrors}
          message={Object.entries(petitionPacketErrors)
            .map(([errorKey, message]) =>
              errorKey === 'file_hash' && message === 'has already been taken'
                ? 'This packet has already been uploaded to Blocks'
                : message
            )
            .map(t)
            .join(', ')}
        />
        {currentShift && currentStep === 'cover_sheet_data' && (
          <ShiftForm shift={currentShift} saveForm={storeCoverInfo} />
        )}
        {currentShift && currentStep === 'upload' && (
          <Upload
            shift={currentShift}
            setFiles={handleUpload}
            setCounty={setCounty}
            disabled={
              Temporal.Instant.from(currentShift.shift_end)
                .until(Temporal.Now.instant())
                .total('hours') <=
                currentShift.turf.options.shifts_uploadable_delay ||
              (currentShift.type === SHIFT_TYPE.PETITION && !county)
            }
            county={county}
          />
        )}
      </ViewContainer>
    </View>
  )
}

export default ShiftBatch
